import { useEffect } from "react";
import { useController } from "react-hook-form";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Select from "../../../Form/Select";
import TextField from "../../../Form/TextField";
import NumberDecimalField from "../../../Form/NumberDecimalField";
import NumberField from "../../../Form/NumberField";
import {
  CLICKS,
  MAXIMIZE_CONVERSION_VALUE,
  MAXIMIZE_CONVERSIONS,
  MAXIMUM_COST_PER_CLICK_PLACEHOLDER,
  TARGET_CPA_PLACEHOLDER,
  TARGET_ROAS_PLACEHOLDER,
  BIDDING_STRATEGY_TYPE_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import ChipOptional from "../../../Chip/ChipOptional";
import { InputAdornment } from "@mui/material";
import { getCurrencyAdAccountPlatform } from "../../../../actions/getters";
import {
  ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION,
  ADS_CAMPAIGN_TYPE_APP_OPTION,
  GOOGLE,
  googleCurrenciesWithDecimals,
} from "../../../../utils/constants";
import { useDispatchApp } from "../../../../lib/redux";

const BIDDING_STRATEGY_OPTIONS = [
  { value: "TARGET_SPEND", label: CLICKS },
  { value: "MAXIMIZE_CONVERSIONS", label: MAXIMIZE_CONVERSIONS },
  { value: "MAXIMIZE_CONVERSION_VALUE", label: MAXIMIZE_CONVERSION_VALUE },
  { value: "TARGET_CPA", label: TARGET_CPA_PLACEHOLDER },
];

const LABELS_BY_TYPE = {
  TARGET_SPEND: MAXIMUM_COST_PER_CLICK_PLACEHOLDER,
  MAXIMIZE_CONVERSIONS: MAXIMIZE_CONVERSIONS,
  MAXIMIZE_CONVERSION_VALUE: TARGET_ROAS_PLACEHOLDER,
  TARGET_CPA: TARGET_CPA_PLACEHOLDER,
};

const DEFAULT_TYPE = "MAXIMIZE_CONVERSIONS";

const BiddingStrategyGoogleForm = ({
  name = "biddingStrategy",
  control,
  objective,
  integrationID,
  adAccountID,
  disabled = false,
  errors,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const isAppCampaign = objective === ADS_CAMPAIGN_TYPE_APP_OPTION;

  // Controller for the type select
  const {
    field: { value: typeValue = DEFAULT_TYPE, onChange: onTypeChange },
  } = useController({
    name: `${name}.type`,
    control,
    defaultValue: DEFAULT_TYPE,
  });

  // Controller for the value field
  const {
    field: { value: valueValue, onChange: onValueChange },
  } = useController({
    name: `${name}.value`,
    control,
    defaultValue: "",
  });

  // App campaigns: only MAXIMIZE_CONVERSIONS and TARGET_CPA; reset invalid persisted type
  useEffect(() => {
    if (!isAppCampaign) return;
    const allowedAppTypes = ["MAXIMIZE_CONVERSIONS", "TARGET_CPA"];
    if (!allowedAppTypes.includes(typeValue)) {
      onTypeChange("MAXIMIZE_CONVERSIONS");
    }
  }, [isAppCampaign, typeValue, onTypeChange]);

  // App + maximize conversions: no target value (keep field empty)
  useEffect(() => {
    if (!isAppCampaign || typeValue !== "MAXIMIZE_CONVERSIONS") return;
    onValueChange("");
  }, [isAppCampaign, typeValue, onValueChange]);

  const currency = dispatch(
    getCurrencyAdAccountPlatform({
      platform: GOOGLE,
      adAccountID,
      integrationID,
    })
  );

  const getAdornment = () => {
    if (typeValue === "MAXIMIZE_CONVERSION_VALUE") {
      return "%";
    } else {
      return currency;
    }
  };
  const adornment = getAdornment();

  const filteredOptions = BIDDING_STRATEGY_OPTIONS.filter((option) => {
    if (isAppCampaign) {
      return (
        option.value === "MAXIMIZE_CONVERSIONS" || option.value === "TARGET_CPA"
      );
    }
    if (
      objective === ADS_CAMPAIGN_TYPE_PERFORMANCE_MAX_OPTION &&
      option.value === "TARGET_SPEND"
    ) {
      return false;
    }
    return true;
  });

  const showValueFieldForApp = !isAppCampaign || typeValue === "TARGET_CPA";

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {t(BIDDING_STRATEGY_TYPE_PLACEHOLDER)}
        </Typography>
        <Select
          name={`${name}.type`}
          options={filteredOptions}
          value={typeValue}
          onChange={onTypeChange}
          control={control}
          disabled={disabled}
          doTranslate={true}
          fullWidth
          actionsChange={(newType) => {
            if (isAppCampaign && newType === "MAXIMIZE_CONVERSIONS") {
              onValueChange("");
              return;
            }
            onValueChange(0);
          }}
          variant="filled"
          errors={errors}
        />
      </Grid>
      {showValueFieldForApp && (
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body1">
              {t(LABELS_BY_TYPE[typeValue])}
            </Typography>
            <ChipOptional size="small" />
          </Box>
          <TextField
            name={`${name}.value`}
            value={valueValue}
            onChange={onValueChange}
            InputProps={{
              inputComponent:
                typeValue === "MAXIMIZE_CONVERSION_VALUE" ||
                googleCurrenciesWithDecimals.includes(currency)
                  ? NumberDecimalField
                  : NumberField,
              startAdornment: (
                <InputAdornment position="start">{adornment}</InputAdornment>
              ),
            }}
            control={control}
            disabled={disabled}
            fullWidth
            variant="filled"
            errors={errors}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default BiddingStrategyGoogleForm;
